// Conditional GSAP import to prevent compilation errors
let gsap, ScrollTrigger;

try {
  gsap = require('gsap');
  ScrollTrigger = require('gsap/ScrollTrigger');
  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);
} catch (error) {
  console.warn('GSAP not installed. Run: npm install gsap');
  // Create mock functions to prevent errors
  gsap = {
    timeline: () => ({ 
      to: () => ({ scrollTrigger: {} }),
      fromTo: () => ({ scrollTrigger: {} }),
      set: () => ({ scrollTrigger: {} })
    }),
    to: () => ({ scrollTrigger: {} }),
    fromTo: () => ({ scrollTrigger: {} }),
    set: () => ({ scrollTrigger: {} })
  };
  ScrollTrigger = {
    getAll: () => [],
    refresh: () => {},
    create: () => ({ kill: () => {} })
  };
}

export const initScrollAnimations = () => {
  // Exit early if GSAP is not available
  if (!gsap || !ScrollTrigger || typeof gsap.timeline !== 'function') {
    console.warn('GSAP not properly installed. Animations disabled.');
    return;
  }
  
  try {
    // Clear existing ScrollTriggers
    const triggers = ScrollTrigger.getAll();
    if (triggers && triggers.length > 0) {
      triggers.forEach(trigger => {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      });
    }
  } catch (error) {
    console.warn('Error clearing ScrollTriggers:', error);
  }

  // Hero Section Dramatic Animations
  const heroSection = document.querySelector('#hero');
  if (heroSection) {
    try {
      // Hero content zoom and scale
      gsap.timeline({
        scrollTrigger: {
          trigger: heroSection,
          start: "top top",
          end: "bottom top",
          scrub: 1,
          pin: false,
        }
      })
      .to('.hero-title', {
        scale: 0.8,
        y: 100,
        opacity: 0.3,
        ease: "power2.inOut"
      }, 0)
      .to('.hero-subtitle', {
        scale: 0.7,
        y: 150,
        opacity: 0.2,
        ease: "power2.inOut"
      }, 0)
      .to('.hero-cta', {
        scale: 0.6,
        y: 200,
        opacity: 0.1,
        ease: "power2.inOut"
      }, 0);
    } catch (error) {
      console.warn('Hero section animation error:', error);
    }
  }

  // Services Section - Dramatic Card Animations
  const servicesSection = document.querySelector('#services');
  if (servicesSection) {
    try {
      const serviceCards = servicesSection.querySelectorAll('.stagger-cards > *');
      
      serviceCards.forEach((card, index) => {
        try {
          // Zoom in from different directions
          const startScale = 0.3;
          const startRotation = index % 2 === 0 ? -15 : 15;
          const startY = index % 3 === 0 ? -200 : (index % 3 === 1 ? 200 : 0);
          const startX = index % 2 === 0 ? -150 : 150;

          gsap.fromTo(card, 
            {
              scale: startScale,
              rotation: startRotation,
              y: startY,
              x: startX,
              opacity: 0
            },
            {
              scale: 1,
              rotation: 0,
              y: 0,
              x: 0,
              opacity: 1,
              duration: 1.5,
              ease: "power4.out",
              scrollTrigger: {
                trigger: card,
                start: "top 80%",
                end: "top 20%",
                scrub: 1,
              }
            }
          );

          // Expansion on hover scroll
          gsap.to(card, {
            scale: 1.05,
            rotation: 2,
            duration: 0.3,
            scrollTrigger: {
              trigger: card,
              start: "top 50%",
              end: "bottom 50%",
              scrub: 1,
            }
          });
        } catch (error) {
          console.warn(`Service card ${index} animation error:`, error);
        }
      });

      // Services section collapse effect
      gsap.timeline({
        scrollTrigger: {
          trigger: servicesSection,
          start: "bottom top",
          end: "bottom -50%",
          scrub: 1,
        }
      })
      .to('.services-header', {
        scale: 0.7,
        y: -100,
        opacity: 0.5,
        ease: "power2.inOut"
      }, 0);
    } catch (error) {
      console.warn('Services section animation error:', error);
    }
  }

  // Remaining animations with comprehensive error handling
  try {
    // Analysis Section - Typography Animations
    const analysisSection = document.querySelector('#analysis');
    if (analysisSection) {
      try {
        // Dramatic text reveals
        const analysisTitle = analysisSection.querySelector('h2');
        if (analysisTitle) {
          gsap.fromTo(analysisTitle,
            {
              scale: 3,
              rotation: 5,
              y: 300,
              opacity: 0,
              skewY: 10
            },
            {
              scale: 1,
              rotation: 0,
              y: 0,
              opacity: 1,
              skewY: 0,
              duration: 2,
              ease: "power4.out",
              scrollTrigger: {
                trigger: analysisTitle,
                start: "top 90%",
                end: "top 30%",
                scrub: 1,
              }
            }
          );
        }

        // Analysis cards collapse/expand
        const analysisCards = analysisSection.querySelectorAll('.analysis-card');
        analysisCards.forEach((card, index) => {
          try {
            gsap.fromTo(card,
              {
                scale: 0.1,
                rotation: -180,
                y: 500,
                opacity: 0
              },
              {
                scale: 1,
                rotation: 0,
                y: 0,
                opacity: 1,
                duration: 1.8,
                ease: "back.out(1.7)",
                scrollTrigger: {
                  trigger: card,
                  start: "top 85%",
                  end: "top 25%",
                  scrub: 1,
                }
              }
            );
          } catch (error) {
            console.warn(`Analysis card ${index} animation error:`, error);
          }
        });
      } catch (error) {
        console.warn('Analysis section animation error:', error);
      }
    }

    // Stats Section - Number Explosions
    const statsSection = document.querySelector('#stats');
    if (statsSection) {
      try {
        const statNumbers = statsSection.querySelectorAll('.stat-number');
        
        statNumbers.forEach((stat, index) => {
          try {
            gsap.fromTo(stat,
              {
                scale: 0,
                rotation: 720,
                opacity: 0
              },
              {
                scale: 1,
                rotation: 0,
                opacity: 1,
                duration: 2,
                ease: "elastic.out(1, 0.5)",
                scrollTrigger: {
                  trigger: stat,
                  start: "top 80%",
                  end: "top 40%",
                  scrub: 1,
                }
              }
            );
          } catch (error) {
            console.warn(`Stat number ${index} animation error:`, error);
          }
        });
      } catch (error) {
        console.warn('Stats section animation error:', error);
      }
    }

    // Contact Section - Final Collapse
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      try {
        gsap.timeline({
          scrollTrigger: {
            trigger: contactSection,
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          }
        })
        .to('.contact-content', {
          scale: 0.9,
          y: -50,
          opacity: 0.8,
          ease: "power2.inOut"
        }, 0);
      } catch (error) {
        console.warn('Contact section animation error:', error);
      }
    }

    // Parallax Background Elements
    const parallaxElements = document.querySelectorAll('.parallax-element');
    parallaxElements.forEach((element, index) => {
      try {
        const speed = 0.5 + (index * 0.1);
        gsap.to(element, {
          yPercent: -50 * speed,
          ease: "none",
          scrollTrigger: {
            trigger: element,
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          }
        });
      } catch (error) {
        console.warn(`Parallax element ${index} animation error:`, error);
      }
    });

    // Typography Scroll Effects
    const typographyElements = document.querySelectorAll('.typography-play');
    typographyElements.forEach((element, index) => {
      try {
        gsap.timeline({
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            end: "bottom 20%",
            scrub: 1,
          }
        })
        .to(element, {
          scale: 1.2,
          letterSpacing: "0.1em",
          fontWeight: "700",
          ease: "power2.inOut"
        }, 0)
        .to(element, {
          scale: 1,
          letterSpacing: "0.02em",
          fontWeight: "400",
          ease: "power2.inOut"
        }, 0.5);
      } catch (error) {
        console.warn(`Typography element ${index} animation error:`, error);
      }
    });

    // Section Transitions
    const sections = document.querySelectorAll('section');
    sections.forEach((section, index) => {
      try {
        if (index > 0) {
          gsap.fromTo(section,
            {
              scale: 0.8,
              y: 100,
              opacity: 0
            },
            {
              scale: 1,
              y: 0,
              opacity: 1,
              duration: 1,
              ease: "power2.out",
              scrollTrigger: {
                trigger: section,
                start: "top 90%",
                end: "top 30%",
                scrub: 1,
              }
            }
          );
        }
      } catch (error) {
        console.warn(`Section ${index} transition error:`, error);
      }
    });
  } catch (error) {
    console.warn('General animation error:', error);
  }
};

export const refreshScrollTriggers = () => {
  ScrollTrigger.refresh();
};
